import React, { useState, useEffect, useRef } from 'react';
import { 
  MapPin, Navigation, Sparkles, Compass, Palmtree, Mountain, Building2, 
  Coffee, Camera, Footprints, ShoppingBag, Waves, Sun, Calendar, Clock, ArrowRight, 
  Loader2, CheckCircle2, ChevronLeft, User, Heart, Users, Home, Leaf, Droplets, Binoculars, Bot,
  Activity, Star, Backpack, Tent, Utensils, Wine, Landmark, Map, Car, Bus, Bike
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

// --- UPGRADED STATIC FALLBACK DATABASE ---
const destinationDB = [
  { id: 1, name: "Goa", state: "Goa", companions: ["Friends", "Couple", "Solo"], vibes: ["Relaxing", "Party"], landscape: "Beaches", activities: ["Water Sports", "Food & Drinks", "Sightseeing"], pace: ["Balanced", "Slow"], stay: ["5-Star Luxury", "Cozy Homestays"], pop: "Iconic Spots", img: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?auto=format&fit=crop&w=800&q=80", desc: "India's pocket-sized paradise. Pristine beaches, vibrant nightlife, and Portuguese heritage." },
  { id: 2, name: "Manali", state: "Himachal Pradesh", companions: ["Friends", "Couple", "Family"], vibes: ["Adventure", "Nature"], landscape: "Mountains", activities: ["Trekking", "Sightseeing", "Photography"], pace: ["Fast & Active", "Balanced"], stay: ["5-Star Luxury", "Cozy Homestays", "Glamping & Nature"], pop: "Iconic Spots", img: "https://images.unsplash.com/photo-1605649487212-4d43bf8d7904?auto=format&fit=crop&w=800&q=80", desc: "A high-altitude Himalayan resort town known for its cool climate and snow-capped peaks." },
  { id: 3, name: "Udaipur", state: "Rajasthan", companions: ["Couple", "Family"], vibes: ["Cultural", "Relaxing"], landscape: "City/Heritage", activities: ["Sightseeing", "Food & Drinks", "Shopping"], pace: ["Balanced", "Slow"], stay: ["5-Star Luxury", "Heritage & Palaces"], pop: "Iconic Spots", img: "https://images.unsplash.com/photo-1615836245337-f839dffdbac3?auto=format&fit=crop&w=800&q=80", desc: "The City of Lakes. Majestic palaces, romantic boat rides, and rich royal history." },
  { id: 4, name: "Rishikesh", state: "Uttarakhand", companions: ["Solo", "Friends"], vibes: ["Adventure", "Spiritual"], landscape: "Mountains", activities: ["Water Sports", "Trekking", "Sightseeing"], pace: ["Fast & Active", "Balanced"], stay: ["Cozy Homestays", "Glamping & Nature"], pop: "Mixed", img: "https://images.unsplash.com/photo-1605640840605-14ac1855827b?auto=format&fit=crop&w=800&q=80", desc: "The Yoga Capital of the World, sitting on the banks of the holy Ganges river." },
  { id: 5, name: "Munnar", state: "Kerala", companions: ["Couple", "Family"], vibes: ["Nature", "Relaxing"], landscape: "Mountains", activities: ["Sightseeing", "Trekking", "Wildlife"], pace: ["Slow", "Balanced"], stay: ["5-Star Luxury", "Glamping & Nature"], pop: "Iconic Spots", img: "https://images.unsplash.com/photo-1593693397690-362cb9666fc2?auto=format&fit=crop&w=800&q=80", desc: "Endless expanses of tea plantations, pristine valleys, and exotic flora and fauna." },
  { id: 6, name: "Spiti Valley", state: "Himachal Pradesh", companions: ["Friends", "Solo"], vibes: ["Adventure", "Nature"], landscape: "Mountains", activities: ["Trekking", "Photography", "Sightseeing"], pace: ["Fast & Active"], stay: ["Cozy Homestays", "Glamping & Nature"], pop: "Hidden Gems", img: "https://images.unsplash.com/photo-1616422285623-14c12ff31404?auto=format&fit=crop&w=800&q=80", desc: "A cold desert mountain valley known for its extreme landscapes and ancient Buddhist culture." },
  { id: 7, name: "Ziro Valley", state: "Arunachal Pradesh", companions: ["Solo", "Friends"], vibes: ["Nature", "Cultural"], landscape: "Mountains", activities: ["Photography", "Sightseeing", "Trekking"], pace: ["Slow"], stay: ["Cozy Homestays", "Glamping & Nature"], pop: "Hidden Gems", img: "https://images.unsplash.com/photo-1571536802807-3cabae663a8a?auto=format&fit=crop&w=800&q=80", desc: "A serene, off-the-grid valley famous for its Apatani tribe, pine hills, and rice fields." },
  { id: 8, name: "Wayanad", state: "Kerala", companions: ["Family", "Couple"], vibes: ["Nature", "Relaxing"], landscape: "Forests/Jungles", activities: ["Wildlife", "Trekking", "Sightseeing"], pace: ["Balanced"], stay: ["5-Star Luxury", "Glamping & Nature"], pop: "Mixed", img: "https://images.unsplash.com/photo-1621213038659-4d6cb35d0af9?auto=format&fit=crop&w=800&q=80", desc: "A lush, green district famous for its wildlife sanctuaries, spice plantations, and waterfalls." }
];

// --- AUTOCOMPLETE ENGINE ---
const SearchAutocomplete = ({ value, onChange, placeholder, isDark }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!value || value.length < 2) { setSuggestions([]); return; }
    const delayDebounceFn = setTimeout(async () => {
      try {
        const response = await fetch(`/api/get_ola_search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: value }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data?.results) {
            setSuggestions(data.results.map((item) => item.formatted_address));
            return;
          }
        }

        const res = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(value)}&limit=5`);
        const data = await res.json();
        if (data?.features) {
          const parsed = data.features.filter(f => f.properties.country === 'India' || f.properties.countrycode === 'IN')
            .map(f => [f.properties.name, f.properties.state].filter(Boolean).join(', '));
          setSuggestions([...new Set(parsed)]);
        }
      } catch(e) {}
    }, 400); 
    return () => clearTimeout(delayDebounceFn);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%', position: 'relative' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <MapPin size={20} color="#0284c7" style={{ position: 'absolute', left: '16px' }} />
        <input 
          type="text" 
          placeholder={placeholder} 
          value={value} 
          onChange={(e) => { onChange(e.target.value); setShowDropdown(true); }} 
          onFocus={() => { if(suggestions.length > 0) setShowDropdown(true); }} 
          style={{ 
            width: '100%', padding: '16px 16px 16px 48px', borderRadius: '14px', 
            border: isDark ? '2px solid #4b5563' : '2px solid #e5e7eb', outline: 'none', 
            fontSize: '16px', fontWeight: 'bold', color: isDark ? 'white' : '#111827', 
            backgroundColor: isDark ? '#111827' : '#f9fafb', transition: 'all 0.2s', boxSizing: 'border-box' 
          }} 
        />
      </div>
      {showDropdown && suggestions.length > 0 && (
        <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', listStyle: 'none', margin: '8px 0 0 0', padding: '5px 0', zIndex: 50, border: isDark ? '1px solid #374151' : '1px solid #e5e7eb' }}>
          {suggestions.map((s, i) => (
            <li key={i} onClick={() => { onChange(s); setShowDropdown(false); }} style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: i === suggestions.length - 1 ? 'none' : (isDark ? '1px solid #374151' : '1px solid #f3f4f6'), fontSize: '14px', color: isDark ? '#d1d5db' : '#374151', display: 'flex', alignItems: 'center', gap: '10px' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#374151' : '#f0fdf4'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDark ? '#1f2937' : 'white'}>
              <Navigation size={14} color="#16a34a" /> {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const fetchWikiImage = async (title) => {
  if (!title) return null;
  try {
    const sParams = `origin=*&action=query&list=search&srsearch=${encodeURIComponent(title + ' India')}&srlimit=1&format=json`;
    const sRes = await fetch(`https://en.wikipedia.org/w/api.php?${sParams}`).then(r => r.json());
    if (sRes.query?.search?.length > 0) {
      const actualTitle = sRes.query.search[0].title;
      const params = `origin=*&action=query&format=json&prop=pageimages&pithumbsize=800&titles=${encodeURIComponent(actualTitle)}`;
      const res = await fetch(`https://en.wikipedia.org/w/api.php?${params}`).then(r => r.json());
      const pages = res.query?.pages;
      if (pages) {
        const pageId = Object.keys(pages)[0];
        if (pages[pageId].thumbnail?.source) {
          const src = pages[pageId].thumbnail.source;
          return src.startsWith('//') ? 'https:' + src : src;
        }
      }
    }
    return null;
  } catch (e) {
    return null;
  }
};

const TripDiscovererPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme(); // 👈 Access Theme State

  const [step, setStep] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [isAiGenerated, setIsAiGenerated] = useState(false);
  
  const [quizData, setQuizData] = useState({
    origin: '',
    companions: '',
    vibe: '',
    landscape: '',
    activities: [],
    duration: '',
    pace: '',
    accommodation: '',
    food: '',
    popularity: '',
    transport: ''
  });

  const companions = [
    { label: 'Solo', icon: User, desc: "Just me & my backpack" },
    { label: 'Couple', icon: Heart, desc: "Romantic or duo trip" },
    { label: 'Family', icon: Home, desc: "Kids or parents" },
    { label: 'Friends', icon: Users, desc: "Group of buddies" }
  ];

  const vibes = [
    { label: 'Nature & Wildlife', icon: Palmtree, color: '#15803d', bg: '#dcfce7', darkBg: '#064e3b' },
    { label: 'Adventure', icon: Mountain, color: '#b91c1c', bg: '#fee2e2', darkBg: '#450a0a' },
    { label: 'Relaxing', icon: Sun, color: '#ea580c', bg: '#ffedd5', darkBg: '#431407' },
    { label: 'Cultural', icon: Building2, color: '#6d28d9', bg: '#f3e8ff', darkBg: '#3b0764' },
    { label: 'Party', icon: Sparkles, color: '#be185d', bg: '#fce7f3', darkBg: '#500724' },
    { label: 'Spiritual', icon: Compass, color: '#0284c7', bg: '#e0f2fe', darkBg: '#082f49' }
  ];

  const landscapes = [
    { label: 'Mountains', icon: Mountain },
    { label: 'Beaches', icon: Waves },
    { label: 'Desert', icon: Sun },
    { label: 'Forests/Jungles', icon: Leaf },
    { label: 'City/Heritage', icon: Building2 }
  ];

  const activities = [
    { label: 'Sightseeing', icon: Camera },
    { label: 'Trekking', icon: Footprints },
    { label: 'Food & Drinks', icon: Coffee },
    { label: 'Water Sports', icon: Droplets },
    { label: 'Shopping', icon: ShoppingBag },
    { label: 'Wildlife', icon: Binoculars }
  ];

  const durations = [
    { label: 'Quick Getaway', sub: '1-3 Days', icon: Clock },
    { label: 'Standard Trip', sub: '4-7 Days', icon: Calendar },
    { label: 'Long Vacation', sub: '1+ Week', icon: Compass }
  ];

  const paces = [
    { label: 'Fast & Active', sub: 'Packed schedule, see it all', icon: Activity },
    { label: 'Balanced Mix', sub: 'Some activities, some downtime', icon: Map },
    { label: 'Slow & Relaxed', sub: 'Sleep in, take it easy', icon: Coffee }
  ];

  const accommodations = [
    { label: '5-Star Luxury', sub: 'Pamper me with best resorts', icon: Star },
    { label: 'Heritage & Palaces', sub: 'Live like royalty in forts', icon: Landmark },
    { label: 'Cozy Homestays', sub: 'Authentic vibes with locals', icon: Home },
    { label: 'Glamping & Nature', sub: 'Wilderness with comfort', icon: Tent }
  ];

  const foods = [
    { label: 'Street Food Explorer', icon: Utensils },
    { label: 'Fine Dining & Cafes', icon: Wine },
    { label: 'Pure Veg / Sattvic', icon: Leaf },
    { label: 'Anything works', icon: Coffee }
  ];

  const popularities = [
    { label: 'Iconic Spots', sub: 'Must-see famous landmarks', icon: Landmark },
    { label: 'Mixed', sub: 'Popular spots + secrets', icon: Map },
    { label: 'Hidden Gems', sub: 'Off-the-grid, zero crowds', icon: Compass }
  ];

  const transports = [
    { label: 'Chauffeured Car', sub: 'Maximum comfort and ease', icon: Car },
    { label: 'Rent a Bike/Scooter', sub: 'Wind in hair, freedom', icon: Bike },
    { label: 'Local Transit & Autos', sub: 'Travel the local way', icon: Bus },
    { label: 'Walking & Cycling', sub: 'Slow, sustainable exploration', icon: Footprints }
  ];

  const handleCalculate = async () => {
    setStep(12); 
    setIsCalculating(true);
    setIsAiGenerated(false);

    const currentMonth = new Date().toLocaleString('default', { month: 'long' });

    const prompt = `You are an expert Indian travel planner. 
    User Profile:
    - Origin: ${quizData.origin}
    - Companions: ${quizData.companions}
    - Vibe: ${quizData.vibe}
    - Landscape: ${quizData.landscape}
    - Activities: ${quizData.activities.join(', ')}
    - Duration: ${quizData.duration}
    - Pace: ${quizData.pace}
    - Accommodation: ${quizData.accommodation}
    - Food: ${quizData.food}
    - Spot Popularity: ${quizData.popularity}
    - Transport Preference: ${quizData.transport}
    - Current Season: ${currentMonth} (CRITICAL: Weather must be good right now)

    Task: Suggest the top 3 Indian destinations that perfectly match this highly specific profile.
    Return ONLY a valid JSON array of exactly 3 objects. Do not include markdown or \`\`\`json.
    Structure MUST match exactly:
    [
      {
        "name": "Destination Name",
        "state": "State Name",
        "landscape": "${quizData.landscape}",
        "activities": ["Top Activity 1"],
        "desc": "A compelling 2-sentence description of why this is perfect for them based on their answers."
      }
    ]`;

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: prompt }],
          max_tokens: 2048
        })
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Chat API error: ${response.status} ${error}`);
      }

      const data = await response.json();
      const botResponse = data.reply || '';

      const startIdx = botResponse.indexOf('[');
      const endIdx = botResponse.lastIndexOf(']');
      if (startIdx === -1 || endIdx === -1) throw new Error("No JSON array in response");
      const cleanJSON = botResponse.substring(startIdx, endIdx + 1);

      const aiDestinations = JSON.parse(cleanJSON);

      const withImages = await Promise.all(aiDestinations.map(async (dest, idx) => {
        let img = await fetchWikiImage(dest.name);
        return { 
          ...dest, 
          id: idx + 1, 
          img: img || "https://images.unsplash.com/photo-1506461883276-594a12b11cf3?auto=format&fit=crop&w=800&q=80" 
        };
      }));

      setRecommendations(withImages);
      setIsAiGenerated(true);

    } catch (error) {
      console.error("AI Generation failed, falling back to static database:", error);
      
      let scoredDestinations = destinationDB.map(dest => {
        let score = 0;
        if (dest.companions.includes(quizData.companions)) score += 2;
        if (dest.vibes.includes(quizData.vibe)) score += 3; 
        if (dest.landscape === quizData.landscape) score += 3;
        if (dest.pace && dest.pace.includes(quizData.pace)) score += 2;
        if (dest.stay && dest.stay.includes(quizData.accommodation)) score += 1.5;
        if (dest.pop === quizData.popularity) score += 2;

        quizData.activities.forEach(act => {
          if (dest.activities.includes(act)) score += 1.5;
        });
        score += Math.random() * 0.5; 
        return { ...dest, matchScore: score };
      });

      scoredDestinations.sort((a, b) => b.matchScore - a.matchScore);
      setRecommendations(scoredDestinations.slice(0, 3));
      setIsAiGenerated(false);

    } finally {
      setIsCalculating(false);
      setStep(13); 
    }
  };

  const handleSelectDestination = (dest) => {
    navigate('/plan', { 
      state: { 
        prefillPackage: { 
          title: `${quizData.vibe} trip to ${dest.name}`, 
          location: `${quizData.origin.split(',')[0]} to ${dest.name}`,
          description: `A ${quizData.duration} ${quizData.companions.toLowerCase()} trip focusing on ${quizData.activities.join(', ')}. Pace: ${quizData.pace}.`
        },
        presetDestination: dest.name 
      } 
    });
  };

  const toggleActivity = (act) => {
    setQuizData(prev => {
      if (prev.activities.includes(act)) return { ...prev, activities: prev.activities.filter(a => a !== act) };
      if (prev.activities.length >= 3) return prev; 
      return { ...prev, activities: [...prev.activities, act] };
    });
  };

  const renderStepContent = () => {
    switch(step) {
      case 0: return (
          <div style={{ textAlign: 'center', animation: 'fadeIn 0.5s ease-out' }}>
            <div style={{ width: '70px', height: '70px', backgroundColor: isDark ? '#082f49' : '#e0f2fe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
              <Sparkles size={32} color={isDark ? '#38bdf8' : '#0284c7'} />
            </div>
            <h1 style={{ fontSize: 'clamp(28px, 4vw, 36px)', fontWeight: '900', color: isDark ? '#f9fafb' : '#111827', marginBottom: '12px' }}>Find Your Perfect Trip</h1>
            <p style={{ fontSize: '15px', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '30px', lineHeight: '1.6', maxWidth: '500px', margin: '0 auto 30px auto' }}>
              Answer 11 quick questions about your travel style, and we will scan 100+ destinations to find your ultimate tailored getaway.
            </p>
            <button onClick={() => setStep(1)} style={{ padding: '14px 32px', borderRadius: '50px', backgroundColor: '#111827', color: 'white', fontSize: '16px', fontWeight: 'bold', border: isDark ? '1px solid #4b5563' : 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', transition: 'transform 0.2s' }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
              Start the Quiz <ArrowRight size={18} />
            </button>
          </div>
        );
      case 1: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>Where are you starting from?</h2>
            <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '25px' }}>We need to know your origin to suggest realistic routes.</p>
            <SearchAutocomplete value={quizData.origin} onChange={(val) => setQuizData({...quizData, origin: val})} placeholder="e.g., New Delhi, India" isDark={isDark} />
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setStep(2)} disabled={quizData.origin.length < 3} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: quizData.origin.length < 3 ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: quizData.origin.length < 3 ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: quizData.origin.length < 3 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );
      case 2: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px' }}>Who are you traveling with?</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
              {companions.map((comp, idx) => {
                const isSel = quizData.companions === comp.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, companions: comp.label})} style={{ padding: '16px', borderRadius: '16px', border: isSel ? `2px solid #0284c7` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#082f49' : '#e0f2fe') : (isDark ? '#1f2937' : '#f9fafb'), cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', textAlign: 'left', boxSizing: 'border-box' }}>
                    <comp.icon size={24} color={isSel ? '#0284c7' : '#9ca3af'} />
                    <div><div style={{ fontSize: '15px', fontWeight: 'bold', color: isSel ? (isDark ? '#38bdf8' : '#0369a1') : (isDark ? '#f9fafb' : '#1f2937') }}>{comp.label}</div><div style={{ fontSize: '11px', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '2px' }}>{comp.desc}</div></div>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(3)} disabled={!quizData.companions} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: !quizData.companions ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: !quizData.companions ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: !quizData.companions ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );
      case 3: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px' }}>What kind of vibe do you want?</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
              {vibes.map((v, idx) => {
                const isSel = quizData.vibe === v.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, vibe: v.label})} style={{ padding: '16px', borderRadius: '16px', border: isSel ? `2px solid ${v.color}` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? v.darkBg : v.bg) : (isDark ? '#1f2937' : '#f9fafb'), cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', boxSizing: 'border-box' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: isDark ? '#111827' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}><v.icon size={20} color={v.color} /></div>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: isSel ? v.color : (isDark ? '#d1d5db' : '#4b5563'), textAlign: 'center' }}>{v.label}</span>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(2)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(4)} disabled={!quizData.vibe} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: !quizData.vibe ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: !quizData.vibe ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: !quizData.vibe ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );
      case 4: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px' }}>Choose your landscape</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
              {landscapes.map((l, idx) => {
                const isSel = quizData.landscape === l.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, landscape: l.label})} style={{ padding: '14px', borderRadius: '14px', border: isSel ? `2px solid #16a34a` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#1f2937' : 'white'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', boxSizing: 'border-box' }}>
                    <l.icon size={20} color={isSel ? '#16a34a' : '#9ca3af'} />
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: isSel ? (isDark ? '#a7f3d0' : '#15803d') : (isDark ? '#d1d5db' : '#4b5563') }}>{l.label}</span>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(3)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(5)} disabled={!quizData.landscape} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: !quizData.landscape ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: !quizData.landscape ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: !quizData.landscape ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );
      case 5: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>What do you want to do?</h2>
            <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '20px' }}>Select up to 3 activities.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {activities.map((act, idx) => {
                const isSel = quizData.activities.includes(act.label);
                return (
                  <button key={idx} onClick={() => toggleActivity(act.label)} style={{ padding: '12px 20px', borderRadius: '50px', border: isSel ? `2px solid #0284c7` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#082f49' : '#e0f2fe') : (isDark ? '#1f2937' : 'white'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxSizing: 'border-box' }}>
                    <act.icon size={18} color={isSel ? '#0284c7' : '#9ca3af'} />
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: isSel ? (isDark ? '#38bdf8' : '#0369a1') : (isDark ? '#d1d5db' : '#4b5563') }}>{act.label}</span>
                    {isSel && <CheckCircle2 size={14} color="#0284c7" style={{ marginLeft: '4px' }}/>}
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(4)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(6)} disabled={quizData.activities.length === 0} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: quizData.activities.length === 0 ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: quizData.activities.length === 0 ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: quizData.activities.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );
      case 6: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px' }}>How long is the trip?</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {durations.map((dur, idx) => {
                const isSel = quizData.duration === dur.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, duration: dur.label})} style={{ padding: '16px', borderRadius: '16px', border: isSel ? `2px solid #16a34a` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#1f2937' : '#f9fafb'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px', textAlign: 'left', boxSizing: 'border-box' }}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: isSel ? '#16a34a' : (isDark ? '#374151' : '#e5e7eb'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><dur.icon size={20} color={isSel ? 'white' : '#6b7280'} /></div>
                    <div><h4 style={{ fontSize: '16px', fontWeight: 'bold', color: isSel ? (isDark ? '#a7f3d0' : '#15803d') : (isDark ? '#f9fafb' : '#111827'), margin: '0 0 3px 0' }}>{dur.label}</h4><p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0 }}>{dur.sub}</p></div>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(5)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(7)} disabled={!quizData.duration} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: !quizData.duration ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: !quizData.duration ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: !quizData.duration ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );
      case 7: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px' }}>What's your travel pace?</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {paces.map((p, idx) => {
                const isSel = quizData.pace === p.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, pace: p.label})} style={{ padding: '16px', borderRadius: '16px', border: isSel ? `2px solid #0284c7` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#082f49' : '#e0f2fe') : (isDark ? '#1f2937' : '#f9fafb'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px', textAlign: 'left', boxSizing: 'border-box' }}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: isSel ? '#0284c7' : (isDark ? '#374151' : '#e5e7eb'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><p.icon size={20} color={isSel ? 'white' : '#6b7280'} /></div>
                    <div><h4 style={{ fontSize: '16px', fontWeight: 'bold', color: isSel ? (isDark ? '#38bdf8' : '#0369a1') : (isDark ? '#f9fafb' : '#111827'), margin: '0 0 3px 0' }}>{p.label}</h4><p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0 }}>{p.sub}</p></div>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(6)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(8)} disabled={!quizData.pace} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: !quizData.pace ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: !quizData.pace ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: !quizData.pace ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );

      case 8: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>Accommodation Style?</h2>
            <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '20px' }}>Where do you want to rest your head at night?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {accommodations.map((acc, idx) => {
                const isSel = quizData.accommodation === acc.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, accommodation: acc.label})} style={{ padding: '16px', borderRadius: '16px', border: isSel ? `2px solid #16a34a` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#1f2937' : '#f9fafb'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px', textAlign: 'left', transition: 'all 0.2s', boxSizing: 'border-box' }}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: isSel ? '#16a34a' : (isDark ? '#374151' : '#e5e7eb'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><acc.icon size={20} color={isSel ? 'white' : '#6b7280'} /></div>
                    <div><h4 style={{ fontSize: '16px', fontWeight: 'bold', color: isSel ? (isDark ? '#a7f3d0' : '#15803d') : (isDark ? '#f9fafb' : '#111827'), margin: '0 0 3px 0' }}>{acc.label}</h4><p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0 }}>{acc.sub}</p></div>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(7)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(9)} disabled={!quizData.accommodation} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: !quizData.accommodation ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: !quizData.accommodation ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: !quizData.accommodation ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );

      case 9: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px' }}>Food Preferences?</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
              {foods.map((f, idx) => {
                const isSel = quizData.food === f.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, food: f.label})} style={{ padding: '14px', borderRadius: '14px', border: isSel ? `2px solid #0284c7` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#082f49' : '#e0f2fe') : (isDark ? '#1f2937' : 'white'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', boxSizing: 'border-box' }}>
                    <f.icon size={18} color={isSel ? '#0284c7' : '#9ca3af'} />
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: isSel ? (isDark ? '#38bdf8' : '#0369a1') : (isDark ? '#d1d5db' : '#4b5563') }}>{f.label}</span>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(8)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(10)} disabled={!quizData.food} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: !quizData.food ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: !quizData.food ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: !quizData.food ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );

      case 10: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px' }}>Famous or Offbeat?</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {popularities.map((pop, idx) => {
                const isSel = quizData.popularity === pop.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, popularity: pop.label})} style={{ padding: '16px', borderRadius: '16px', border: isSel ? `2px solid #16a34a` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#1f2937' : '#f9fafb'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px', textAlign: 'left', boxSizing: 'border-box' }}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: isSel ? '#16a34a' : (isDark ? '#374151' : '#e5e7eb'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><pop.icon size={20} color={isSel ? 'white' : '#6b7280'} /></div>
                    <div><h4 style={{ fontSize: '16px', fontWeight: 'bold', color: isSel ? (isDark ? '#a7f3d0' : '#15803d') : (isDark ? '#f9fafb' : '#111827'), margin: '0 0 3px 0' }}>{pop.label}</h4><p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0 }}>{pop.sub}</p></div>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(9)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={() => setStep(11)} disabled={!quizData.popularity} style={{ padding: '12px 24px', borderRadius: '10px', backgroundColor: !quizData.popularity ? (isDark ? '#374151' : '#e5e7eb') : '#0284c7', color: !quizData.popularity ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: !quizData.popularity ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>Next Step <ArrowRight size={16} /></button>
            </div>
          </div>
        );

      case 11: return (
          <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>Transport Preference?</h2>
            <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '20px' }}>How do you want to get around locally?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {transports.map((t, idx) => {
                const isSel = quizData.transport === t.label;
                return (
                  <button key={idx} onClick={() => setQuizData({...quizData, transport: t.label})} style={{ padding: '16px', borderRadius: '16px', border: isSel ? `2px solid #0284c7` : (isDark ? '2px solid #374151' : '2px solid #e5e7eb'), backgroundColor: isSel ? (isDark ? '#082f49' : '#e0f2fe') : (isDark ? '#1f2937' : '#f9fafb'), cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px', textAlign: 'left', transition: 'all 0.2s', boxSizing: 'border-box' }}>
                    <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: isSel ? '#0284c7' : (isDark ? '#374151' : '#e5e7eb'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><t.icon size={20} color={isSel ? 'white' : '#6b7280'} /></div>
                    <div><h4 style={{ fontSize: '16px', fontWeight: 'bold', color: isSel ? (isDark ? '#38bdf8' : '#0369a1') : (isDark ? '#f9fafb' : '#111827'), margin: '0 0 3px 0' }}>{t.label}</h4><p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0 }}>{t.sub}</p></div>
                  </button>
                )
              })}
            </div>
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => setStep(10)} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}><ChevronLeft size={16}/> Back</button>
              <button onClick={handleCalculate} disabled={!quizData.transport} style={{ padding: '12px 28px', borderRadius: '10px', backgroundColor: !quizData.transport ? (isDark ? '#374151' : '#e5e7eb') : (isDark ? '#374151' : '#111827'), color: !quizData.transport ? '#9ca3af' : 'white', fontSize: '15px', fontWeight: 'bold', border: isDark ? '1px solid #4b5563' : 'none', cursor: !quizData.transport ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Find My Destination <Sparkles size={16} />
              </button>
            </div>
          </div>
        );

      case 12: 
        return (
          <div style={{ textAlign: 'center', padding: '50px 0', animation: 'fadeIn 0.5s ease-out' }}>
            <Loader2 size={50} color={isDark ? '#38bdf8' : '#0284c7'} className="animate-spin" style={{ margin: '0 auto 20px auto' }} />
            <h2 style={{ fontSize: '22px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>
              Our Team is exploring the map...
            </h2>
            <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280' }}>
              Factoring in current weather conditions for your {quizData.landscape} trip.
            </p>
          </div>
        );

      case 13: 
        return (
          <div style={{ animation: 'fadeIn 0.6s ease-out' }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: isAiGenerated ? (isDark ? '#082f49' : '#e0f2fe') : (isDark ? '#064e3b' : '#ecfdf5'), color: isAiGenerated ? (isDark ? '#38bdf8' : '#0284c7') : (isDark ? '#a7f3d0' : '#15803d'), padding: '6px 14px', borderRadius: '50px', fontWeight: 'bold', fontSize: '13px', marginBottom: '12px' }}>
                {isAiGenerated ? <Bot size={16} /> : <CheckCircle2 size={16} />} 
                {isAiGenerated ? "Curated Matches" : "Matches Found"}
              </div>
              <h2 style={{ fontSize: 'clamp(26px, 4vw, 32px)', fontWeight: '900', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>Your Perfect Getaways</h2>
              <p style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280' }}>Based on your origin in {quizData.origin.split(',')[0]}, here are the best options for your trip.</p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {recommendations.map((dest, idx) => (
                <div key={dest.id} style={{ display: 'flex', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', overflow: 'hidden', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.06)', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', flexDirection: window.innerWidth < 600 ? 'column' : 'row', boxSizing: 'border-box' }}>
                  
                  <div style={{ position: 'relative', width: window.innerWidth < 600 ? '100%' : '240px', height: window.innerWidth < 600 ? '180px' : 'auto', flexShrink: 0, backgroundColor: isDark ? '#374151' : '#e5e7eb' }}>
                    <img src={dest.img} alt={dest.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', top: '12px', left: '12px', backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', color: 'white', padding: '5px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={12} color="#38bdf8" /> {dest.state}
                    </div>
                    {idx === 0 && (
                      <div style={{ position: 'absolute', top: '12px', right: '12px', backgroundColor: '#f59e0b', color: 'white', padding: '5px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 4px 10px rgba(245,158,11,0.4)' }}>
                        <Sparkles size={12} /> #1 Match
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', color: isDark ? '#38bdf8' : '#0284c7', backgroundColor: isDark ? '#082f49' : '#e0f2fe', padding: '3px 8px', borderRadius: '4px' }}>{dest.landscape}</span>
                      <span style={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', color: isDark ? '#d8b4fe' : '#6d28d9', backgroundColor: isDark ? '#3b0764' : '#f3e8ff', padding: '3px 8px', borderRadius: '4px' }}>{quizData.vibe}</span>
                      {dest.activities && dest.activities.length > 0 && (
                        <span style={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', color: isDark ? '#a7f3d0' : '#15803d', backgroundColor: isDark ? '#064e3b' : '#dcfce7', padding: '3px 8px', borderRadius: '4px' }}>Great for {dest.activities[0]}</span>
                      )}
                    </div>
                    
                    <h3 style={{ fontSize: '20px', fontWeight: '900', color: isDark ? '#f9fafb' : '#111827', margin: '0 0 8px 0' }}>{dest.name}</h3>
                    <p style={{ fontSize: '13px', color: isDark ? '#d1d5db' : '#4b5563', lineHeight: '1.5', margin: '0 0 16px 0' }}>{dest.desc}</p>
                    
                    <button onClick={() => handleSelectDestination(dest)} style={{ alignSelf: 'flex-start', padding: '10px 20px', borderRadius: '10px', backgroundColor: isDark ? '#374151' : '#111827', color: 'white', fontWeight: 'bold', fontSize: '13px', border: isDark ? '1px solid #4b5563' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'background 0.2s' }} onMouseEnter={e=>e.currentTarget.style.backgroundColor=isDark ? '#4b5563' : '#374151'} onMouseLeave={e=>e.currentTarget.style.backgroundColor=isDark ? '#374151' : '#111827'}>
                      Select & Plan Itinerary <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <button onClick={() => { setStep(0); setQuizData({ origin:'', companions:'', vibe:'', landscape:'', activities:[], duration:'', pace: '', accommodation: '', food: '', popularity: '', transport: '' }); }} style={{ background: 'none', border: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px' }}>
                Start Over
              </button>
            </div>
          </div>
        );

      default: return null;
    }
  };

  const progressPercentage = step === 0 ? 0 : step >= 12 ? 100 : ((step) / 11) * 100;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f3f4f6', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: '80px', paddingTop: '90px', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
      
      <div style={{ maxWidth: '650px', margin: '0 auto', padding: '0 16px', boxSizing: 'border-box' }}>
        
        {step > 0 && step < 12 && (
          <div style={{ marginBottom: '30px', animation: 'fadeIn 0.5s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#6b7280' }}>
              <span>Step {step} of 11</span>
              <span>{Math.round(progressPercentage)}%</span>
            </div>
            <div style={{ height: '6px', backgroundColor: isDark ? '#374151' : '#e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
              <div style={{ width: `${progressPercentage}%`, height: '100%', backgroundColor: '#0284c7', transition: 'width 0.4s ease-out' }} />
            </div>
          </div>
        )}

        <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '24px', padding: step === 0 ? '40px 24px' : '30px 20px', boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.5)' : '0 20px 40px rgba(0,0,0,0.08)', minHeight: '350px', position: 'relative', overflow: 'hidden', border: isDark ? '1px solid #374151' : 'none', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
          
          {step < 12 && (
            <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '130px', height: '130px', backgroundColor: isDark ? '#082f49' : '#f0f9ff', borderRadius: '50%', zIndex: 0, opacity: 0.5 }} />
          )}

          <div style={{ position: 'relative', zIndex: 1 }}>
            {renderStepContent()}
          </div>
        </div>

      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default TripDiscovererPage;